import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useScrollLock } from '../../hooks/useScrollLock';
import { AlertCircleIcon, XIcon } from './Icons';
import { useSession } from '../contexts/SessionContext';

const SessionWarningModal: React.FC = () => {
    const { isWarning, resetTimer } = useSession();
    const [timeLeft, setTimeLeft] = useState(0);

    const warningDurationSec = 5 * 60; // Total time between warning and timeout

    useScrollLock(isWarning);
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        if (isWarning) {
            setIsMounted(true);
            setTimeLeft(warningDurationSec);
        } else {
            const timer = setTimeout(() => setIsMounted(false), 300);
            return () => clearTimeout(timer);
        }
    }, [isWarning, warningDurationSec]);

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (isWarning && timeLeft > 0) {
            interval = setInterval(() => {
                setTimeLeft(prev => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [isWarning, timeLeft]);

    if (!isMounted && !isWarning) return null;

    const formatTime = (seconds: number) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    return ReactDOM.createPortal(
        <div className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-all duration-300 ${isWarning ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity" />
            
            <div className={`relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full mx-auto overflow-hidden transition-all duration-300 transform ${isWarning ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'}`}>
                
                <button 
                    onClick={resetTimer}
                    className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 transition-colors"
                >
                    <XIcon className="h-5 w-5" />
                </button>

                <div className="p-6">
                    <div className="flex flex-col items-center text-center">
                        <div className="p-3 rounded-lg mb-4 text-amber-500 bg-amber-100 dark:bg-amber-900/30">
                            <AlertCircleIcon className="h-8 w-8" />
                        </div>
                        
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                            Session Expiring
                        </h3>

                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                            For your security, your session will expire in <span className="font-bold text-gray-900 dark:text-white">{formatTime(timeLeft)}</span> due to inactivity. Do you need more time?
                        </p>

                        <button
                            onClick={resetTimer}
                            className="w-full px-4 py-2.5 rounded-lg font-semibold text-sm text-white shadow-md transition-all active:scale-95 bg-primary hover:bg-primary-hover shadow-primary/20"
                        >
                            Stay Signed In
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default SessionWarningModal;
